import { useState, useRef, useEffect, useCallback } from "react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { ChevronLeft, ChevronRight } from "lucide-react";

const galleryImages = [
  {
    src: "https://customer-assets.emergentagent.com/job_chauffeur-prive-29/artifacts/a0q2gl6l_PHOTO-2026-03-17-11-56-59.jpg",
    alt: "Intérieur Van Mercedes Classe V - Ciel étoilé LED et ambiance lumineuse",
    caption: "Ciel étoilé",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_chauffeur-prive-29/artifacts/ldzey1sp_PHOTO-2026-03-17-11-39-36.jpg",
    alt: "Écran multimédia - Netflix, Prime Video, Disney+, YouTube",
    caption: "Écran multimédia",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_chauffeur-prive-29/artifacts/v6akv0pg_PHOTO-2026-03-17-11-34-43.jpg",
    alt: "Ciel étoilé et tablette Spotify à disposition",
    caption: "Tablette & ciel étoilé",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_chauffeur-prive-29/artifacts/5of6zt1v_PHOTO-2026-03-17-11-53-57.jpg",
    alt: "Van Mercedes Classe V décoré pour un mariage - vue de face",
    caption: "Mariage",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_chauffeur-prive-29/artifacts/ujm59s3x_PHOTO-2026-03-17-11-54-07.jpg",
    alt: "Van Mercedes Classe V décoré pour un mariage - vue latérale",
    caption: "Mariage",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_chauffeur-prive-29/artifacts/exc8axmr_2024-11-06-2.jpeg",
    alt: "Van Mercedes Classe V au port de Brest",
    caption: "Mercedes Classe V",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_chauffeur-prive-29/artifacts/wl10q124_2024-11-06.jpg",
    alt: "Intérieur cuir du Van Mercedes Classe V 7 places",
    caption: "Intérieur cuir",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_chauffeur-prive-29/artifacts/ngmo09dh_PHOTO-2026-03-17-11-57-56.jpg",
    alt: "Van Mercedes Classe V devant sculpture artistique en bord de mer",
    caption: "Bord de mer",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_chauffeur-prive-29/artifacts/9zhwyyd9_PHOTO-2026-03-17-11-58-55.jpg",
    alt: "Van Mercedes Classe V devant l'hôtel Le Conti",
    caption: "Hôtel Le Conti",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_chauffeur-prive-29/artifacts/3e996uys_PHOTO-2026-03-17-11-59-14.jpg",
    alt: "Van Mercedes Classe V devant le phare de Saint-Mathieu",
    caption: "Phare Saint-Mathieu",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_chauffeur-prive-29/artifacts/sovou40v_PHOTO-2026-03-17-12-00-18.jpg",
    alt: "Van Mercedes Classe V sur la côte bretonne",
    caption: "Côte bretonne",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_chauffeur-prive-29/artifacts/suzk5k5b_PHOTO-2026-03-17-12-01-25.jpg",
    alt: "Van Mercedes Classe V sur une plage du Finistère",
    caption: "Tourisme Finistère",
  },
];

export const GallerySection = () => {
  const [sectionRef, isVisible] = useScrollAnimation(0.1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const trackRef = useRef(null);
  const autoplayRef = useRef(null);
  const isHoveredRef = useRef(false);
  const SLIDE_WIDTH = 520; // px per slide
  const GAP = 20;

  const goTo = useCallback((idx) => {
    const newIdx = ((idx % galleryImages.length) + galleryImages.length) % galleryImages.length;
    setCurrentIndex(newIdx);
  }, []);

  const goNext = useCallback(() => goTo(currentIndex + 1), [currentIndex, goTo]);
  const goPrev = useCallback(() => goTo(currentIndex - 1), [currentIndex, goTo]);

  // Auto-play
  useEffect(() => {
    const startAutoplay = () => {
      autoplayRef.current = setInterval(() => {
        if (!isHoveredRef.current) {
          setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
        }
      }, 3500);
    };

    startAutoplay();
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, []);

  const translateX = -(currentIndex * (SLIDE_WIDTH + GAP));

  return (
    <section
      ref={sectionRef}
      id="galerie"
      className="py-[80px] md:py-[100px] bg-[#f8f7f2] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          className={`flex flex-col md:flex-row md:items-end md:justify-between mb-12 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div>
            <p
              className="text-[12px] tracking-[0.2em] uppercase text-[#c9a84c] mb-4"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Galerie
            </p>
            <h2
              className="text-[30px] md:text-[36px] font-normal text-[#1a1d2e] leading-[1.2] mb-3"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Découvrez notre véhicule
            </h2>
            <div className="gold-line" />
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-3 mt-6 md:mt-0">
            <button
              onClick={goPrev}
              className="w-[48px] h-[48px] flex items-center justify-center border border-[#1a1d2e]/20 text-[#1a1d2e] hover:bg-[#1a1d2e] hover:text-white transition-all duration-200"
              aria-label="Photo précédente"
            >
              <ChevronLeft size={20} strokeWidth={1.5} />
            </button>
            <button
              onClick={goNext}
              className="w-[48px] h-[48px] flex items-center justify-center border border-[#1a1d2e]/20 text-[#1a1d2e] hover:bg-[#1a1d2e] hover:text-white transition-all duration-200"
              aria-label="Photo suivante"
            >
              <ChevronRight size={20} strokeWidth={1.5} />
            </button>
            <span
              className="ml-3 text-[13px] text-[#1a1d2e]/40 tabular-nums"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {currentIndex + 1} / {galleryImages.length}
            </span>
          </div>
        </div>
      </div>

      {/* Carousel Track */}
      <div
        className={`transition-all duration-700 delay-200 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
        onMouseEnter={() => { isHoveredRef.current = true; }}
        onMouseLeave={() => { isHoveredRef.current = false; }}
      >
        <div className="overflow-hidden">
          <div
            ref={trackRef}
            className="flex transition-transform duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
            style={{
              transform: `translateX(calc(40px + ${translateX}px))`,
              gap: `${GAP}px`,
            }}
          >
            {galleryImages.map((image, idx) => (
              <div
                key={idx}
                data-testid={`gallery-slide-${idx}`}
                className="flex-shrink-0 relative group cursor-pointer"
                style={{ width: `${SLIDE_WIDTH}px` }}
              >
                <div className="overflow-hidden border border-[#1a1d2e]/10 group-hover:border-[#c9a84c]/50 transition-colors duration-500">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-[320px] md:h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1a1d2e]/70 to-transparent p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span
                    className="text-[13px] text-white/90"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {image.caption}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-8">
        <div className="flex items-center gap-2">
          {galleryImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goTo(idx)}
              className={`h-[2px] transition-all duration-300 ${
                idx === currentIndex
                  ? "w-[32px] bg-[#c9a84c]"
                  : "w-[16px] bg-[#1a1d2e]/15 hover:bg-[#1a1d2e]/30"
              }`}
              aria-label={`Photo ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
